import { useState, useEffect, useMemo } from 'react';
import { Table, Pagination } from 'react-bootstrap';
import { Box, Button, Typography, Paper, Tooltip } from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import { format, parseISO } from 'date-fns';
import '../../moveinout.scss';

const MoveInOutInspectionListTable = ({
    items = [],
    onEdit,
    onDelete,
    onSendEmail,
    scrollThreshold = 768,
    userRole = 'other',
}) => {
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [currentPage, setCurrentPage] = useState(1);
    const sizePerPage = 10;

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const sortedItems = useMemo(() => {
        return [...items].sort((a, b) => {
            const dateA = a.move_in?.['1']?.inspection_date || a.move_in?.['3']?.inspection_date || a.createdAt;
            const dateB = b.move_in?.['1']?.inspection_date || b.move_in?.['3']?.inspection_date || b.createdAt;
            return new Date(dateB) - new Date(dateA);
        });
    }, [items]);

    const totalPages = Math.ceil(sortedItems.length / sizePerPage);
    const paginatedItems = sortedItems.slice((currentPage - 1) * sizePerPage, currentPage * sizePerPage);


    const formatDate = (date) => {
        if (!date) return '—';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC',
        });
    };

    const getAddress = (row) => {
        const addr = row.property_details?.address || '';
        const unit = row.property_details?.unit_number ? `Unit ${row.property_details.unit_number}` : '';
        return addr && unit ? `${addr} • ${unit}` : addr || unit || 'N/A';
    };

    const getTenantNames = (tenants = []) => tenants.map(t => t.name?.trim()).filter(Boolean).join(', ') || 'N/A';

    const getCompletedByName = (phase) => phase?.['1']?.landlord_name?.trim() || phase?.['3']?.landlord_name?.trim() || '—';

    const isLandlordPartComplete = (phase) => {
        const step1 = phase?.['1'];
        const step3 = phase?.['3'];
        const step1Done = !!(step1?.inspection_date && step1?.landlord_name?.trim() && step1?.landlord_signature?.trim() && step1?.landlord_signature_date);
        const step3Done = !!(step3?.inspection_date && step3?.landlord_name?.trim() && step3?.landlord_signature?.trim() && step3?.landlord_signature_date && step3?.forwarding_address?.trim());
        return step1Done || step3Done;
    };

    const hasTenantSigned = (phase) => !!phase?.['2']?.a?.tenant_signature?.trim();
    const hasTenantDisagreement = (phase) => !!phase?.['2']?.b?.disagreement_reasons?.trim();
    const isPhaseFullySignedOrRefused = (phase) => hasTenantSigned(phase) || hasTenantDisagreement(phase);

    const getMoveInStatus = (row) => {
        const mi = row.move_in;
        if (!mi || !isLandlordPartComplete(mi)) return { text: 'Not Started', color: 'grey.500' };
        if (hasTenantSigned(mi)) return { text: 'Signed', color: 'success.main' };
        if (hasTenantDisagreement(mi)) return { text: 'Disagreement', color: 'error.main' };
        return { text: 'Awaiting Tenant', color: 'warning.main' };
    };

    const getMoveOutStatus = (row) => {
        const mo = row.move_out;
        if (!mo || !mo['1']?.inspection_date) return { text: 'Not Started', color: 'grey.500' };
        if (!isLandlordPartComplete(mo)) return { text: 'In Progress', color: 'info.main' };
        if (hasTenantSigned(mo)) return { text: 'Signed', color: 'success.main' };
        if (hasTenantDisagreement(mo)) return { text: 'Disagreement', color: 'error.main' };
        return { text: 'Awaiting Tenant', color: 'warning.main' };
    };

    return (
        <Paper
            sx={{
                height: '72vh',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            }}
        >
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                <Table bordered hover responsive className="moveinout-table">
                    <thead>
                        <tr>
                            <th className="sticky-col">Tenant(s)</th>
                            <th>Move-In Status</th>
                            <th>Move-Out Status</th>
                            <th>Address & Unit</th>
                            <th>Move-In Date</th>
                            <th>Move-In By</th>
                            <th>Move-Out Date</th>
                            <th>Move-Out By</th>
                            <th className="actions-col">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedItems.length > 0 ? (
                            paginatedItems.map((row) => {
                                const mi = row.move_in;
                                const mo = row.move_out;
                                return (
                                    <tr key={row._id} className="table-row" onClick={() => onEdit(row)} style={{ cursor: 'pointer' }}>
                                        <td className="sticky-col">
                                            <Tooltip title={getTenantNames(row.property_details?.tenants)} arrow>
                                                <Box sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {getTenantNames(row.property_details?.tenants)}
                                                </Box>
                                            </Tooltip>
                                        </td>
                                        <td>
                                            <Typography variant="body2" sx={{ color: getMoveInStatus(row).color, fontWeight: 'medium', fontSize: 12, bgcolor: `${getMoveInStatus(row).color}20`, borderRadius: 1, px: 1.5, py: 0.4, display: 'inline-block' }}>
                                                {getMoveInStatus(row).text}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography variant="body2" sx={{ color: getMoveOutStatus(row).color, fontWeight: 'medium', fontSize: 12, bgcolor: `${getMoveOutStatus(row).color}20`, borderRadius: 1, px: 1.5, py: 0.4, display: 'inline-block' }}>
                                                {getMoveOutStatus(row).text}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Tooltip title={getAddress(row)} arrow><span>{getAddress(row)}</span></Tooltip>
                                        </td>
                                        <td>{formatDate(mi?.['1']?.inspection_date ?? mi?.['3']?.inspection_date)}</td>
                                        <td>{getCompletedByName(mi)}</td>
                                        <td>{formatDate(mo?.['1']?.inspection_date ?? mo?.['3']?.inspection_date)}</td>
                                        <td>{getCompletedByName(mo)}</td>
                                        <td className="actions-col">
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexWrap: 'nowrap',
                                                    gap: 1,
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    minWidth: 'fit-content',
                                                }}
                                            >
                                                <Tooltip title="Edit / View">
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={(e) => { e.stopPropagation(); onEdit(row); }}
                                                        sx={{
                                                            minWidth: 36,
                                                            width: 36,
                                                            height: 22,
                                                            p: 0
                                                        }}
                                                    >
                                                        <FeatherIcon icon="edit-2" size={16} />
                                                    </Button>
                                                </Tooltip>

                                                {userRole === 'company' && (
                                                    <Tooltip title="Delete">
                                                        <Button
                                                            variant="contained"
                                                            color="error"
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); onDelete(row); }}
                                                            sx={{
                                                                minWidth: 36,
                                                                width: 36,
                                                                height: 22,
                                                                p: 0
                                                            }}
                                                        >
                                                            <FeatherIcon icon="trash-2" size={16} />
                                                        </Button>
                                                    </Tooltip>
                                                )}

                                                {onSendEmail && (
                                                    <Tooltip title="Send Email">
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); onSendEmail(row); }}
                                                            sx={{
                                                                minWidth: 36,
                                                                width: 36,
                                                                height: 22,
                                                                p: 0,
                                                                bgcolor: '#9c27b0',
                                                                '&:hover': { bgcolor: '#7b1fa2' }
                                                            }}
                                                        >
                                                            <FeatherIcon icon="mail" size={16} />
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={9}>
                                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                                        <Box textAlign="center" py={4}>
                                            <FeatherIcon icon="home" size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
                                            <Typography variant="h6" color="text.secondary">No Move-In/Out Inspections</Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No inspection records available</Typography>
                                        </Box>
                                    </Box>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Box>

            {totalPages > 1 && (
                <Box sx={{ p: 2, borderTop: '1px solid #dee2e6', bgcolor: '#f8f9fa', display: 'flex', justifyContent: 'center' }}>
                    <Pagination>
                        <Pagination.Prev onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)} disabled={currentPage === 1} />
                        {[...Array(totalPages)].map((_, i) => (
                            <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
                                {i + 1}
                            </Pagination.Item>
                        ))}
                        <Pagination.Next onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} />
                    </Pagination>
                </Box>
            )}
        </Paper>
    );
};

export { MoveInOutInspectionListTable };